import * as React from 'react';

import { DEFAULT_ROLE_OPTIONS_LIMIT } from 'bundles/onboarding-common/constants';
import ChipAndAutocompleteInput from 'bundles/personalization-settings/components/ChipAndAutocompleteInput';
import useDesiredRolesEditor, {
  CREATE_USER_GENERATED_KEY,
} from 'bundles/personalization-settings/hooks/useDesiredRolesEditor';
import type { AvailableRole } from 'bundles/personalization-settings/hooks/useDesiredRolesEditor';
import {
  useDebouncedPersonalizationSearchTracker,
  useTrackPersonalizationButtonClick,
  useTrackPersonalizationSelectChange,
} from 'bundles/personalization-settings/hooks/usePersonalizationEventTracking';
import { removeRole } from 'bundles/personalization-settings/utils/desiredRolesV1';
import type { SelectedRole } from 'bundles/personalization-settings/utils/desiredRolesV1';

import _t from 'i18n!nls/personalization-settings';

export type DesiredRolesEditorProps = {
  /** The learner's currently selected desired roles (controlled). */
  value: SelectedRole[];
  /** Called with the next selection whenever a role is added or removed. */
  onChange: (roles: SelectedRole[]) => void;
  /** Catalog roles the learner can pick from, supplied by the parent container. */
  availableRoles?: readonly AvailableRole[];
  /**
   * Called with the current typeahead text as the learner types. The container
   * uses it to load matching catalog roles into `availableRoles`.
   */
  onInputChange?: (input: string) => void;
  /** True while the container is loading `availableRoles`, so the popover can show a loading state. */
  loading?: boolean;
  /** Called when the learner tries to add a role while already at the limit. */
  onLimitExceededAttempt?: () => void;
  /** Called when the input loses focus, e.g. to dismiss a limit-exceeded error. */
  onBlur?: () => void;
};

const chipKey = (role: SelectedRole) => (role.type === 'DEFAULT' ? `catalog:${role.id}` : `user:${role.name}`);

/**
 * The "Desired roles" editor for the Personalization settings page: a chip
 * group of the learner's selected roles plus a typeahead to add more. The
 * catalog of selectable roles is supplied by the parent via `availableRoles`;
 * a learner can also add a free-text (user-generated) role that isn't in it.
 *
 * Controlled via `value`/`onChange`.
 */
const DesiredRolesEditor = ({
  value,
  onChange,
  availableRoles = [],
  onInputChange,
  loading,
  onLimitExceededAttempt,
  onBlur,
}: DesiredRolesEditorProps) => {
  // Mirrors ChipAndAutocompleteInput's internal input so useDesiredRolesEditor
  // can compute the right options as the learner types.
  const [input, setInput] = React.useState('');

  const { options, addRoleFromOption, addRoleFromInput } = useDesiredRolesEditor({
    value,
    onChange,
    availableRoles,
    input,
  });

  const trackSearchInput = useDebouncedPersonalizationSearchTracker('desired_roles_search');
  const trackButtonClick = useTrackPersonalizationButtonClick();
  const trackSelectChange = useTrackPersonalizationSelectChange();

  const handleInputChange = (next: string) => {
    setInput(next);
    onInputChange?.(next);
    trackSearchInput(next);
  };

  const chips = value.map((role) => ({ key: chipKey(role), label: role.name }));
  const chipKeyToRole = new Map(value.map((role) => [chipKey(role), role]));

  const handleSelectOption = (key: string) => {
    const nextValue = addRoleFromOption(key);
    if (nextValue) {
      trackButtonClick('personalization_settings_add_desired_role');
      trackSelectChange(
        'desired_roles',
        nextValue.map((role) => role.name)
      );
    }
  };

  const handleAddFromInput = (trimmedInput: string) => {
    const nextValue = addRoleFromInput(trimmedInput);
    if (nextValue) {
      trackButtonClick('personalization_settings_add_desired_role');
      trackSelectChange(
        'desired_roles',
        nextValue.map((role) => role.name)
      );
    }
  };

  const handleRemoveChip = (key: string) => {
    const role = chipKeyToRole.get(key);
    if (!role) return;
    const nextValue = removeRole(value, role);
    onChange(nextValue);
    trackButtonClick('personalization_settings_remove_desired_role');
    trackSelectChange(
      'desired_roles',
      nextValue.map((r) => r.name)
    );
  };

  const displayOptions = options.map((o) =>
    o.key === CREATE_USER_GENERATED_KEY
      ? { key: o.key, label: _t('Add “#{label}”', { label: o.label }) }
      : { key: o.key, label: o.label }
  );

  return (
    <ChipAndAutocompleteInput
      chips={chips}
      onRemoveChip={handleRemoveChip}
      options={displayOptions}
      onSelectOption={handleSelectOption}
      onAddFromInput={handleAddFromInput}
      onInputChange={handleInputChange}
      loading={loading}
      labels={{
        chipsGroup: _t('Selected roles'),
        inputPlaceholder: _t('Add a role'),
        addButton: _t('Add role'),
        notFound: _t('No roles found'),
      }}
      limit={DEFAULT_ROLE_OPTIONS_LIMIT}
      onLimitExceededAttempt={onLimitExceededAttempt}
      onInputBlur={onBlur}
    />
  );
};

export default DesiredRolesEditor;
